import { createElement, useEffect, useRef, useState } from 'react'

import {
  type CapabilityKind,
  capabilityConstraints,
  capabilityTag,
  stopCapabilityStream,
} from './capabilityMedia'

type Broker = HTMLElement & {
  setConstraints?: (constraints: MediaStreamConstraints) => void
  stream?: MediaStream | null
  track?: MediaStreamTrack | null
  isValid?: boolean
  invalidReason?: string
}

const TRANSIENT = new Set(['', 'recently_attached', 'intersection_changed'])

export function CapabilityRetry({
  kind,
  label,
  onReady,
  className,
}: {
  kind: CapabilityKind
  label: string
  onReady: () => void
  className?: string
}) {
  const tag = capabilityTag(kind)
  const ref = useRef<Broker>(null)
  const [dead, setDead] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || !tag) return
    try {
      el.setConstraints?.(capabilityConstraints(kind))
    } catch {
      /* UA defaults still recover Block. */
    }
    const go = () => {
      stopCapabilityStream(el.stream)
      try {
        el.track?.stop()
      } catch {
        /* already ended */
      }
      onReady()
    }
    el.addEventListener('stream', go)
    el.addEventListener('track', go)
    el.addEventListener('promptaction', go)
    // recently_attached expires at 500ms; then style/occlusion blockers are real.
    const timer = window.setTimeout(() => {
      if (el.isValid === false && !TRANSIENT.has(el.invalidReason ?? '')) setDead(true)
    }, 800)
    return () => {
      el.removeEventListener('stream', go)
      el.removeEventListener('track', go)
      el.removeEventListener('promptaction', go)
      window.clearTimeout(timer)
    }
  }, [kind, tag, onReady])

  if (!tag || dead) {
    return (
      <button
        type="button"
        className={className ?? 'primary-button primary-button--lg'}
        onClick={onReady}
      >
        {label}
      </button>
    )
  }

  // Never className/style the UA node — PEPC disables clicks on display:block,
  // width:100%, inherited notice styles, and filtered/clipped ancestors.
  return createElement(tag, { ref })
}
